import api, { BASE_URL } from './api';

export interface Image {
    id: string;
    filename: string;
    original_url: string;
    medium_url: string;
    thumb_url: string;
    blurhash: string;
    width: number;
    height: number;
}

export interface Album {
    id: string;
    title: string;
    description: string;
    created_at: string;
    images: Image[];
}

export interface CreateAlbumDto {
    title: string;
    description?: string;
}

const fixImageUrl = (url: string) => {
    if (!url) return url;
    if (url.startsWith('http')) return url;
    return `${BASE_URL}${url}`;
};

const fixImage = (image: Image): Image => ({
    ...image,
    original_url: fixImageUrl(image.original_url),
    medium_url: fixImageUrl(image.medium_url),
    thumb_url: fixImageUrl(image.thumb_url),
});

const fixAlbum = (album: Album): Album => ({
    ...album,
    images: album.images ? album.images.map(fixImage) : [],
});

export const AlbumsService = {
    create: async (data: CreateAlbumDto): Promise<Album> => {
        const response = await api.post<Album>('/albums', data);
        return fixAlbum(response.data);
    },

    findAll: async (page = 1, limit = 24): Promise<{ data: Album[]; total: number }> => {
        const response = await api.get<{ data: Album[]; total: number }>('/albums', {
            params: { page, limit },
        });
        return {
            ...response.data,
            data: response.data.data.map(fixAlbum),
        };
    },

    findOne: async (id: string): Promise<Album> => {
        const response = await api.get<Album>(`/albums/${id}`);
        return fixAlbum(response.data);
    },

    uploadImages: async (albumId: string, files: File[]): Promise<Image[]> => {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });

        const response = await api.post<Image[]>(`/albums/${albumId}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data.map(fixImage);
    },

    delete: async (id: string): Promise<void> => {
        await api.delete(`/albums/${id}`);
    },
};
